import type { ConsentPreferences } from '@/types/cookie-consent';
import { isConsentCookie, isConsentPreferences } from './validation';

/**
 * Retrieves and validates the consent cookie from localStorage.
 * @returns ConsentCookie if valid, null otherwise.
 */
export function getConsentCookie() {
  try {
    const ccCookie = localStorage.getItem('cc_cookie');
    if (!ccCookie || !ccCookie.length) {
      return null;
    }

    const parsed = JSON.parse(ccCookie);
    if (!isConsentCookie(parsed)) {
      console.error('[CookieConsent] Invalid cc_cookie structure', parsed);
      return null;
    }

    console.log('[CookieConsent] Reading stored consent cookie', parsed);
    return parsed;
  } catch (error) {
    console.error('[CookieConsent] Error getting/parsing cc_cookie', error);
    return null;
  }
}

/**
 * Retrieves and validates consent preferences from localStorage.
 * @returns ConsentPreferences if valid, null otherwise.
 */
export function getConsentPreferences() {
  if (!localStorage.getItem('checkedConsent')) {
    return null;
  }

  try {
    const settings = localStorage.getItem('consentSettings');
    if (!settings) {
      return null;
    }

    const parsed = JSON.parse(settings);
    if (!isConsentPreferences(parsed)) {
      console.error(
        '[CookieConsent] Invalid ConsentPreferences structure',
        parsed,
      );
      return null;
    }

    console.log('[CookieConsent] Reading stored consent preferences', parsed);
    return parsed;
  } catch (error) {
    console.error(
      '[CookieConsent] Error getting/parsing consent settings',
      error,
    );
    return null;
  }
}

/**
 * Saves consent preferences and cookie data to localStorage.
 * @param prefs - ConsentPreferences object to persist.
 */
export function saveConsentToStorage(prefs: ConsentPreferences): void {
  try {
    console.log('[CookieConsent] Saving consent preferences to storage', prefs);
    localStorage.setItem('checkedConsent', 'true');
    localStorage.setItem('consentSettings', JSON.stringify(prefs));
    localStorage.setItem(
      'cc_cookie',
      JSON.stringify({
        level: prefs.accepted_categories,
        revision: 0,
        data: null,
        rfc_cookie: false,
      }),
    );
  } catch (error) {
    console.error('[CookieConsent] Error saving consent to storage', error);
  }
}
